import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { ContextMenu } from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import MoveRowModal from "components/modal/move-row-modal"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  focusCellByDataId,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex,
  unfocusAllCells } from "components/table/table-automation"
import { TextEdit } from "components/table/inputs"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
// Controls
import Controls from "components/hospitality/rooms/controls/"
import NotesControls from "components/notes/"
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import roomService, { Room } from "data/room"
import { Inflation } from "data/inflation"
import keyService, * as chord from "data/key"
import propertyService from "data/property"
import { copy, paste, canPaste, getPasteInfo } from "data/lib/copy-paste"
import toggles from "feature-toggles"
// Stylesheets
import { RoomTable_s } from "components/hospitality/rooms/room-styles"

export type ContextRow = {
  roomid: number
  propertyid: number
  sortorder: number
  description: string
  systeminput?: string
}

type PropTypes = {
  tableId: string
   data: Room[]
   inflationData: Inflation[]
   defaults: object
   selectedState: [ ContextRow[], ( selected: ContextRow[] ) => void ]
   flags: { deleteFlag: number, pasteFlag: number }
 }

const RoomTable = (props: PropTypes) => {
  const [ selected, setSelected ] = props.selectedState

  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: 'Move Row', icon: 'pi pi-fw pi-sort-alt', command: e => {
      automationService.broadcast("MODAL_OPENING", {})
      setShowMove(true)
    }},
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: e => { if(contextRow.systeminput) return;
      roomService.copy(contextRow.propertyid, contextRow.roomid, contextRow.description)
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const rooms = await roomService.copies(contextRow.propertyid, contextRow.roomid, n)
        !_.isEmpty(selected) && setSelected(selected.concat(rooms as ContextRow[]))
        focusCellByDataId(props.tableId, contextRow.roomid)
      })
    }},
    {label: selected.length ? 'Delete Rows' : 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if ( contextRow.systeminput ) return;
      const currentRowNum = getFocusedRowIndex() + 1
      showDeleteConfirmation(contextRow, currentRowNum)
    }}
  ]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null)
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({roomid: 0, propertyid: 0, sortorder: 0, description: ''})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const [showMove, setShowMove] = useState(false)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('room')
  const defaults = props.defaults

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const roomEventSubscription = roomService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "ROOM_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
        case e.type === "ROOM_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code)
          }, 500)
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            roomService.create(propertyService.getStoredPropertyId())
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[(pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1];
            if (!currentRow.systeminput) showDeleteConfirmation(currentRow, currentRowNum)
          }
          break
      }
    })

    return function cleanup() {
      carouselSubscription.unsubscribe()
      roomEventSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    setMenu(nonSystemMenu)
  }, [contextRow])

  const persist = (value, field, rowData) => {
    const record = _.omit(_.cloneDeep(rowData), 'index') // strip fields added for table view

    if (field === 'roomdetail'){
      _.set(record, 'roomdetail', value);
    } else {
      _.set(record, field, value);
    }

    roomService.patch(record);
    if (value && (value.state || value.notes !== undefined)) carouselService.updateRowData(record);
  }

  const refresh = async () => {
    const { propertyid } = contextRow
    const mfs = await roomService.fetchRooms(propertyid)
    setSelected( _.intersectionBy( mfs, selected, 'roomid' ) )
  }

  useEffect(() => {
    if ( canPaste( contextCell, selected ) ) {
      nonSystemMenu.splice( 1, 0, {
        label: selected.length ? 'Paste to Rows' : 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => {
          if (selected.length > 1 ) {
            showPasteConfirmation( contextRow, -1 )
            return
          }
        paste(contextCell, contextRow, refresh, 'room', selected)
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu([]) : setMenu(nonSystemMenu)
  }, [contextCell, selected.length])

  useEffect( () => {
    if ( !props.flags.deleteFlag ) return //skip first time
    showDeleteConfirmation(_.first(selected), -1 )
  }, [ props.flags.deleteFlag ] )

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      message: `Would you like to delete the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        if (selected.length > 1 ) {
          const propertyid = _.first(selected).propertyid
          roomService.deleteMultiple(propertyid, _.map(selected, 'roomid'))
        } else {
          roomService.deleteOne(record.propertyid, record.roomid)
        }
        setDeleteConfirmation(null)
        automationService.broadcast("MODAL_CLOSING", {})
        setTimeout(() => focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0), 200)
      },
      rowNum: record.sortorder,
      code: record.description,
      selected: _.map(selected, s => ({code: s.description}))
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.roomid)}}
        copy={copy}
        show={true}
        title={contextRow.description}
      />
    )
  }

  useEffect( () => {
    if ( !props.flags.pasteFlag ) return //skip first time
    if ( !canPaste( contextCell, selected ) ) return
    showPasteConfirmation( contextRow, -1 )
  }, [ props.flags.pasteFlag ] )

  const showPasteConfirmation = ( record: ContextRow, currentRow: number ) => {
    automationService.broadcast( "MODAL_OPENING", {} )
    setDeleteConfirmation( Controls.DeleteConfirmation( {
      show: true,
      title: 'Paste',
      message: `Would you like to paste ${getPasteInfo()._meta.field} data to the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation( null )
        setTimeout( () => automationService.broadcast( "MODAL_CLOSING", {} ), 200 )
      },
      confirm: () => {
        paste( contextCell, contextRow, refresh, 'room', selected )
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      code: record.description,
      selected: _.map(selected, s => ({code: s.description}))
    }))
  }

  function updateContextMenu(originalEvent){
    _.invoke(menuRef.current, "show", originalEvent)
  }

  const rowClassName = (rowData) => {
    return {'' : rowData}
  }

  const data = props.data.sort(function(a, b) {
    return a.sortorder - b.sortorder;
  });

  const inflData = props['inflationData'];

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      <MoveRowModal show={showMove} rowNum={contextRow.sortorder} close={() => setShowMove(false)} move={(kind, value) => {
        if (!_.isFinite(value)) return setShowMove(false)
        const {propertyid, roomid, sortorder} = contextRow
        const maxRows = _.size(data)
        const destination = {
          'down': Math.min(sortorder + 1, maxRows),
          'up': Math.max(sortorder - 1, 1),
          'to': Math.min(maxRows, Math.max(value, 1))
        }[kind]

        roomService.move(propertyid, roomid, destination).then(() => setShowMove(false))
      }}/>
      {inflData.length > 0 && <RoomTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth={ toggles.multiselect() ? "90px" : "45px" }
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setSelected([])
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
          selection={selected} onSelectionChange={(e) => {
            const selected = _.filter(e.value, (it) => it.systeminput !== "systemImmutable")
            setSelected(selected)
          }}
        >
          { toggles.multiselect() && <Column selectionMode="multiple" frozen={ true } style={ { minWidth: "45px", paddingLeft: "8px" } } /> }
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column"
            filter
            header="Room Description"
            {...field('description')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Rooms/Area"
            {...field('roomdetail', (rowData, props) => { return (
              <CarouselTrigger
                field="roomdetail"
                rowData={rowData}
                title="Rooms/Area"
                persist={persist}
                controls={{
                  basic: {control: Controls.RoomsAreaBasic, name: "Basic"},
                  detailed: {control: Controls.RoomsAreaDetailed, name: 'Detailed'}
                }}
                basicReadonly={['rooms', 'area']}
                hover={{
                  basic: [{key: ['roomdetail', 'rooms'], label: 'Rooms'}, {key: ['roomdetail', 'area'], label: 'Area'}],
                  detailed: {multiple: [{key: ['roomdetail', 'date'], label: 'Date'},{key: ['roomdetail', 'rooms'], label: 'Rooms'},{key: ['roomdetail', 'area'], label: 'Area'}
                  ]}
                }}
              />)}
            )}
          />
          <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="Occupancy"
            {...field('occupancydetail', (rowData, props) => { return (
              <CarouselTrigger
                field="occupancydetail"
                rowData={rowData}
                title="Occupancy"
                persist={persist}
                controls={{
                  basic: {control: Controls.OccupancyBasic, name: "Basic"},
                  detailedYr: {control: Controls.OccupancyDetailedAnnual, name: "Detailed Annual"},
                  detailedMo: {control: Controls.OccupancyDetailedMonthly, name: "Detailed Monthly"}
                }}
                basicReadonly={['occup']}
                hover={{
                  basic: [{key: ['occupancydetail', 'occup'], label: 'Occupancy %'}],
                  detailedYr: {multiple: [{key: ['occupancydetail', 'year'], label: 'Year'}, {key: ['occupancydetail', 'occup'], label: 'Amt'}]},
                  detailedMo: []
                }}
              />)}
            )}
          />
          <Column style={{minWidth: "180px"}} className="p-editable-column"
            header="ADR"
            {...field('adrdetail', (rowData, props) => { return (
              <CarouselTrigger
                field="adrdetail"
                rowData={rowData}
                title="ADR"
                persist={persist}
                namedItems={{inflation: inflData}}
                defaults={defaults}
                controls={{
                  basic: {control: Controls.ADRBasic, name: "Basic"},
                  detailedYr: {control: Controls.ADRDetailedAnnual, name: "Detailed Annual"},
                  detailedMo: {control: Controls.ADRDetailedMonthly, name: "Detailed Monthly"}
                }}
                basicReadonly={['amt']}
                hover={{
                  basic: [{key: ['adrdetail', 'amt'], label: 'ADR'},{key: ['adrdetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'}],
                  detailedYr: {single: [{key: ['adrdetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'}], multiple: [{key: ['adrdetail', 'year'], label: 'Year'}, {key: ['adrdetail', 'amt'], label: 'Amt'}]},
                  detailedMo: {single: [{key: ['adrdetail', 'inflation', 'inflationNamedItemId'], label: 'Inflation'}]}
                }}
              />)}
            )}
          />
          <Column style={{width: "150px"}} className="p-editable-column"
            header="Notes"
            {...field('notedetail', (rowData, props) =>
              <CarouselTrigger
                field="notedetail"
                rowData={rowData}
                title='Room'
                persist={persist}
                controls={{single: {control: NotesControls.NotesEdit, name: "Notes"}}}
                componentAtRest={NotesControls.NotesDisplay}
              />
            )}
          />
        </DataTable>
        { carouselData && carouselData['show'] && <CarouselModal code="description" data={carouselData} key="carousel-modal" width={615} /> }
        { deleteConfirmation }
        { duplicateConfirmation }
        { data.length ? <TableAutomation tableId={props.tableId}/> : null }
        <span className="selection-count">{ selected.length ? `Selected Rows: ${selected.length}` : null }</span>
      </RoomTable_s>}
    </div>
  )
}

export default RoomTable
